import { lazy } from 'react';
import { RouteObject, Navigate } from 'react-router-dom';
import DashboardLayout from '../components/layout/DashboardLayout';
import RoleGuard from '../components/guards/RoleGuard';

const HomePage = lazy(() => import('../pages/home/page'));
const LoginPage = lazy(() => import('../pages/login/page'));
const PasswordResetPage = lazy(() => import('../pages/login/resetpage'));
const ForgotPasswordPage = lazy(() => import('../pages/login/ForgotPasswordPage'));
const DashboardPage = lazy(() => import('../pages/dashboard/page'));
const StudentsPage = lazy(() => import('../pages/students/page'));
const TeachersPage = lazy(() => import('../pages/teachers/page'));
const ParentsPage = lazy(() => import('../pages/parents/page'));
const ClassesPage = lazy(() => import('../pages/classes/page'));
const SubjectsPage = lazy(() => import('../pages/subjects/page'));
const TimetablePage = lazy(() => import('../pages/timetable/page'));
const AttendancePage = lazy(() => import('../pages/attendance/page'));
const ExamsPage = lazy(() => import('../pages/exams/page'));
const ResultsPage = lazy(() => import('../pages/results/page'));
const TransportPage = lazy(() => import('../pages/transport/page'));
const TeacherTransportPage = lazy(() => import('../pages/teacher-transport/page'));
const ReportCardPage = lazy(() => import('../pages/report-card/page'));
const AnnouncementsPage = lazy(() => import('../pages/announcements/page'));
const ParentAnnouncementsPage = lazy(() => import('../pages/parent-announcements/page'));
const ParentTimetablePage = lazy(() => import('../pages/parent-timetable/page'));
const ParentTransportPage = lazy(() => import('../pages/parent-transport/page'));
const ParentAttendancePage = lazy(() => import('../pages/parent-attendance/page'));
const EventsPage = lazy(() => import('../pages/events/page'));
const StudentResultsPage = lazy(() => import('../pages/student-results/page'));
const ClassNotesPage = lazy(() => import('../pages/class-notes/page'));
const StudentNotesPage = lazy(() => import('../pages/student-notes/page'));
const LessonNotesPage = lazy(() => import('../pages/lesson-notes/page'));
const AdminLessonNotesPage = lazy(() => import('../pages/admin-lesson-notes/page'));
const SchoolFeesPage = lazy(() => import('../pages/school-fees/page'));
const ContactMessagesPage = lazy(() => import('../pages/contact-messages/page'));
const NotFoundPage = lazy(() => import('../pages/NotFound'));
const AdmissionsPage = lazy(() => import('../pages/admission/page'));
const AdmissionApplicationsPage = lazy(() => import('../pages/admission-applications/page'));

const ADMIN_ONLY = ['admin'];
const STUDENT_ONLY = ['student'];
const ADMIN_PARENT = ['admin', 'parent', 'student'];
const ADMIN_TEACHER = ['admin', 'teacher'];
const FEES_ROLES = ['admin', 'parent', 'student'];
const PARENT_ONLY = ['parent'];
const TEACHER_ONLY = ['teacher'];

const routes: RouteObject[] = [
  {
    path: '/',
    element: <HomePage />,
  },
  {
    path: '/admission',
    element: <AdmissionsPage />,
  },
  {
    path: '/login',
    element: <LoginPage />,
  },
  {
    path: '/reset-password',
    element: <PasswordResetPage />,
  },
  {
    path: '/forgotpassword',
    element: <ForgotPasswordPage />,
  },
  {
    element: <DashboardLayout />,
    children: [
      { path: '/dashboard', element: <DashboardPage /> },
      {
        path: '/students',
        element: (
          <RoleGuard allowedRoles={ADMIN_ONLY}>
            <StudentsPage />
          </RoleGuard>
        ),
      },
      {
        path: '/teachers',
        element: (
          <RoleGuard allowedRoles={ADMIN_ONLY}>
            <TeachersPage />
          </RoleGuard>
        ),
      },
      {
        path: '/parents',
        element: (
          <RoleGuard allowedRoles={ADMIN_ONLY}>
            <ParentsPage />
          </RoleGuard>
        ),
      },
      {
        path: '/classes',
        element: (
          <RoleGuard allowedRoles={ADMIN_ONLY}>
            <ClassesPage />
          </RoleGuard>
        ),
      },
      {
        path: '/subjects',
        element: (
          <RoleGuard allowedRoles={ADMIN_ONLY}>
            <SubjectsPage />
          </RoleGuard>
        ),
      },
      {
        path: '/transport',
        element: (
          <RoleGuard allowedRoles={ADMIN_ONLY}>
            <TransportPage />
          </RoleGuard>
        ),
      },
      {
        path: '/teacher-transport',
        element: (
          <RoleGuard allowedRoles={TEACHER_ONLY}>
            <TeacherTransportPage />
          </RoleGuard>
        ),
      },
      {
        path: '/report-card',
        element: (
          <RoleGuard allowedRoles={ADMIN_PARENT}>
            <ReportCardPage />
          </RoleGuard>
        ),
      },
      {
        path: '/announcements',
        element: (
          <RoleGuard allowedRoles={ADMIN_TEACHER}>
            <AnnouncementsPage />
          </RoleGuard>
        ),
      },
      {
        path: '/parent-announcements',
        element: (
          <RoleGuard allowedRoles={PARENT_ONLY}>
            <ParentAnnouncementsPage />
          </RoleGuard>
        ),
      },
      {
        path: '/parent-timetable',
        element: (
          <RoleGuard allowedRoles={PARENT_ONLY}>
            <ParentTimetablePage />
          </RoleGuard>
        ),
      },
      {
        path: '/parent-transport',
        element: (
          <RoleGuard allowedRoles={PARENT_ONLY}>
            <ParentTransportPage />
          </RoleGuard>
        ),
      },
      {
        path: '/parent-attendance',
        element: (
          <RoleGuard allowedRoles={PARENT_ONLY}>
            <ParentAttendancePage />
          </RoleGuard>
        ),
      },
      { path: '/timetable', element: <TimetablePage /> },
      {
        path: '/attendance',
        element: (
          <RoleGuard allowedRoles={ADMIN_TEACHER}>
            <AttendancePage />
          </RoleGuard>
        ),
      },
      {
        path: '/exams',
        element: (
          <RoleGuard allowedRoles={ADMIN_ONLY}>
            <ExamsPage />
          </RoleGuard>
        ),
      },
      {
        path: '/results',
        element: (
          <RoleGuard allowedRoles={ADMIN_TEACHER}>
            <ResultsPage />
          </RoleGuard>
        ),
      },
      {
        path: '/student-results',
        element: (
          <RoleGuard allowedRoles={STUDENT_ONLY}>
            <StudentResultsPage />
          </RoleGuard>
        ),
      },
      {
        path: '/class-notes',
        element: (
          <RoleGuard allowedRoles={ADMIN_ONLY}>
            <ClassNotesPage />
          </RoleGuard>
        ),
      },
      {
        path: '/student-notes',
        element: (
          <RoleGuard allowedRoles={STUDENT_ONLY}>
            <StudentNotesPage />
          </RoleGuard>
        ),
      },
      {
        path: '/lesson-notes',
        element: (
          <RoleGuard allowedRoles={TEACHER_ONLY}>
            <LessonNotesPage />
          </RoleGuard>
        ),
      },
      {
        path: '/admin-lesson-notes',
        element: (
          <RoleGuard allowedRoles={ADMIN_ONLY}>
            <AdminLessonNotesPage />
          </RoleGuard>
        ),
      },
      {
        path: '/school-fees',
        element: (
          <RoleGuard allowedRoles={FEES_ROLES}>
            <SchoolFeesPage />
          </RoleGuard>
        ),
      },
      {
        path: '/admission-applications',
        element: (
          <RoleGuard allowedRoles={ADMIN_ONLY}>
            <AdmissionApplicationsPage />
          </RoleGuard>
        ),
      },
      {
        path: '/contact-messages',
        element: (
          <RoleGuard allowedRoles={ADMIN_ONLY}>
            <ContactMessagesPage />
          </RoleGuard>
        ),
      },
      { path: '/events', element: <EventsPage /> },
    ],
  },
  {
    path: '/404',
    element: <NotFoundPage />,
  },
  {
    path: '*',
    element: <Navigate to="/404" replace />,
  },
];

export default routes;
